import React, { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { gql, useQuery } from '@apollo/client';
import { useOrganization } from '../contexts/OrganizationContext';
import { GET_ORGANIZATION_USAGE, GET_ORGANIZATION_MEMBERS } from '../graphql/organizations';

const GET_USER_STATS = gql`
  query GetUserStats {
    contexts(limit: 1000) {
      id
    }
  }
`;

export function SettingsPage() {
  const { user } = useUser();
  const { currentOrganization } = useOrganization();
  const { data } = useQuery(GET_USER_STATS);
  const [loadingBilling, setLoadingBilling] = useState(false);

  // Get organization usage data
  const { data: usageData } = useQuery(GET_ORGANIZATION_USAGE, {
    variables: { organizationId: currentOrganization?.id },
    skip: !currentOrganization,
  });

  // Get organization members to check if current user is admin
  const { data: membersData } = useQuery(GET_ORGANIZATION_MEMBERS, {
    variables: { organizationId: currentOrganization?.id },
    skip: !currentOrganization,
  });

  const contextsCreated = data?.contexts?.length || 0;
  const usage = usageData?.organizationUsage;
  const members = membersData?.organizationMembers || [];

  // Check if current user is admin
  const currentUserMember = members.find((m: any) => m.userId === user?.id);
  const isAdmin = currentUserMember?.role === 'admin';

  // Get plan info from organization
  const userPlan = currentOrganization?.plan || 'free';
  const stripeCustomerId = null; // Will be set after first subscription

  const handleManageBilling = async () => {
    if (!stripeCustomerId) {
      // No subscription yet, redirect to pricing page
      window.location.href = '/pricing';
      return;
    }

    setLoadingBilling(true);
    try {
      const token = user ? await (user as any).getToken() : undefined;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/stripe/customer-portal?customerId=${stripeCustomerId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const { url } = await response.json();
      window.location.href = url; // Redirect to Stripe customer portal
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setLoadingBilling(false);
    }
  };

  const planLimits = {
    free: {
      contexts: 25,
      teamMembers: 1,
      projects: 3,
      versionHistory: '30 days',
    },
    pro: {
      contexts: 'Unlimited',
      teamMembers: 10,
      projects: 'Unlimited',
      versionHistory: 'Unlimited',
    },
    enterprise: {
      contexts: 'Unlimited',
      teamMembers: 'Unlimited',
      projects: 'Unlimited',
      versionHistory: 'Unlimited',
    },
  };

  const currentLimits = planLimits[userPlan as keyof typeof planLimits] || planLimits.free;
  const isNearLimit = userPlan === 'free' && (usage?.currentContexts || contextsCreated) >= 20;

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-8">Account Settings</h1>

      {/* Profile Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Profile
        </h2>
        <div className="space-y-3">
          <div className="flex items-center">
            <span className="text-gray-600 w-32">Email:</span>
            <span className="font-medium">{user?.primaryEmailAddress?.emailAddress}</span>
          </div>
          <div className="flex items-center">
            <span className="text-gray-600 w-32">Name:</span>
            <span className="font-medium">{user?.fullName || 'Not set'}</span>
          </div>
          <div className="flex items-center">
            <span className="text-gray-600 w-32">User ID:</span>
            <span className="font-mono text-sm text-gray-500">{user?.id}</span>
          </div>
        </div>
      </div>

      {/* Organization & Plan Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          Organization & Plan
        </h2>
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <span className="text-gray-600 mr-2">Organization:</span>
            <span className="font-semibold">{currentOrganization?.name || 'No organization'}</span>
          </div>
          <div className="flex items-center mb-2">
            <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
              userPlan === 'enterprise'
                ? 'bg-purple-100 text-purple-800'
                : userPlan === 'pro'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}>
              {userPlan === 'enterprise' ? 'Enterprise Plan' : userPlan === 'pro' ? 'Pro Plan' : 'Free Plan'}
            </span>
            {userPlan === 'pro' && (
              <span className="ml-3 text-gray-600">$49/month</span>
            )}
            {userPlan === 'enterprise' && (
              <span className="ml-3 text-gray-600">Custom pricing</span>
            )}
          </div>
          {userPlan === 'free' && (
            <p className="text-sm text-gray-600">
              Upgrade to Pro for up to 10 team members and unlimited contexts
            </p>
          )}
        </div>

        {/* Organization Usage Stats */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="font-semibold mb-3 text-sm text-gray-600 uppercase">Organization Usage</h3>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-gray-700">Team Members</span>
                <span className="font-medium">
                  {userPlan === 'free' || userPlan === 'pro'
                    ? `${usage?.currentMembers || 1} / ${currentLimits.teamMembers}`
                    : `${usage?.currentMembers || 1} (Unlimited)`}
                </span>
              </div>
              {usage?.memberUsagePercent !== undefined && usage.memberUsagePercent > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      usage.memberUsagePercent >= 90 ? 'bg-red-500' : usage.memberUsagePercent >= 70 ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(usage.memberUsagePercent, 100)}%` }}
                  />
                </div>
              )}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-gray-700">Projects</span>
                <span className="font-medium">
                  {userPlan === 'free'
                    ? `${usage?.currentProjects || 0} / ${currentLimits.projects}`
                    : `${usage?.currentProjects || 0} (Unlimited)`}
                </span>
              </div>
              {usage?.projectUsagePercent !== undefined && usage.projectUsagePercent > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      usage.projectUsagePercent >= 90 ? 'bg-red-500' : usage.projectUsagePercent >= 70 ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(usage.projectUsagePercent, 100)}%` }}
                  />
                </div>
              )}
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-gray-700">Contexts</span>
                <span className="font-medium">
                  {userPlan === 'free'
                    ? `${usage?.currentContexts || contextsCreated} / ${currentLimits.contexts}`
                    : `${usage?.currentContexts || contextsCreated} (Unlimited)`}
                </span>
              </div>
              {usage?.contextUsagePercent !== undefined && usage.contextUsagePercent > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      usage.contextUsagePercent >= 90 ? 'bg-red-500' : usage.contextUsagePercent >= 70 ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.min(usage.contextUsagePercent, 100)}%` }}
                  />
                </div>
              )}
            </div>

            {isNearLimit && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                <p className="text-yellow-800">
                  ⚠️ You're using {usage?.currentContexts || contextsCreated} of {currentLimits.contexts} contexts.
                  <a href="/pricing" className="ml-1 underline font-semibold">Upgrade to Pro</a> for unlimited contexts.
                </p>
              </div>
            )}

            <div className="flex justify-between items-center">
              <span className="text-gray-700">Version History</span>
              <span className="font-medium">{currentLimits.versionHistory}</span>
            </div>
          </div>
        </div>

        {userPlan === 'free' && (
          <div className="mt-4">
            <a
              href="/pricing"
              className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Upgrade to Pro
            </a>
          </div>
        )}
      </div>

      {/* Billing Section (Admin Only) */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          Billing & Subscription
          {isAdmin && (
            <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Admin Only</span>
          )}
        </h2>

        {!isAdmin ? (
          <div className="bg-gray-50 border border-gray-200 rounded p-4">
            <p className="text-gray-700 text-sm">
              Only organization administrators can manage billing and subscriptions. Contact your organization admin to upgrade or change your plan.
            </p>
          </div>
        ) : userPlan === 'pro' || userPlan === 'enterprise' ? (
          <>
            <p className="text-gray-600 mb-4">
              Manage your subscription, update payment method, view invoices, or cancel your plan.
            </p>
            <button
              onClick={handleManageBilling}
              disabled={loadingBilling}
              className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loadingBilling ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Loading...
                </span>
              ) : (
                'Manage Billing'
              )}
            </button>
            <p className="text-sm text-gray-500 mt-3">
              Opens Stripe customer portal in a new window
            </p>
          </>
        ) : (
          <>
            <p className="text-gray-600 mb-4">
              You're currently on the free plan. Upgrade to Pro to unlock unlimited features.
            </p>
            <a
              href="/pricing"
              className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              View Pricing Plans
            </a>
          </>
        )}
      </div>

      {/* Usage Stats */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold mb-2 flex items-center text-blue-900">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Account Statistics
        </h3>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <div className="text-2xl font-bold text-blue-900">{contextsCreated}</div>
            <div className="text-sm text-blue-700">Total Contexts Created</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-900">
              {new Date(user?.createdAt || Date.now()).toLocaleDateString()}
            </div>
            <div className="text-sm text-blue-700">Member Since</div>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="font-semibold mb-2 text-red-900">Danger Zone</h3>
        <p className="text-sm text-red-700 mb-4">
          Need to delete your account? This action cannot be undone.
        </p>
        <button
          onClick={() => {
            if (window.confirm('Are you sure you want to delete your account? This will delete all your contexts and cannot be undone.')) {
              alert('Account deletion coming soon. For now, please contact support.');
            }
          }}
          className="border border-red-300 text-red-700 px-4 py-2 rounded hover:bg-red-100 transition-colors text-sm"
        >
          Delete Account
        </button>
      </div>
    </div>
  );
}
